// src/components/Legal/TermsOfService.js
import React from "react";
import { Container, Typography, Box, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom"; // Make sure you have react-router-dom installed
import { Helmet } from "react-helmet-async";

export default function TermsOfService() {
  return (
    <>
    <Helmet>
      <title>Terms of Service | FlyBnB</title>
      <meta name="description" content="Read FlyBnB's terms of service to understand the rules and guidelines for using our platform." />
      <link rel="canonical" href="https://flybnb.io/termsofservice" />
    </Helmet>
    <Container maxWidth="md" sx={{ my: 6 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        FlyBnB Terms of Service
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Last updated: May 1, 2025 {/* Ensure this date is correct */}
      </Typography>

      <Box sx={{ mt: 4, "& > *": { mb: 3 } }}>
        {/* Section 1 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          1. Acceptance of Terms
        </Typography>
        <Typography paragraph>
          By accessing or using the Flybnb.io website and its services (the
          "Service"), you agree to be bound by these Terms of Service ("Terms")
          and our{" "}
          <MuiLink component={Link} to="/privacy" color="primary">
            {" "}
            {/* Ensure this path is correct */}
            Privacy Policy
          </MuiLink>
          . If you disagree with any part of the terms, then you may not access
          the Service. The Service is provided by the team behind the
          "Ours2Discover" YouTube channel.
        </Typography>

        {/* Section 2 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          2. Description of Service
        </Typography>
        <Typography paragraph>
          Flybnb.io is a travel planning tool that allows users to search for
          and compare flight options and short-term accommodation listings in
          one interface. The Service aims to aggregate publicly available
          information to assist users in planning their trips by showing
          combined cost estimates and providing links to book directly with the
          respective providers (e.g., Skyscanner, Airbnb).
        </Typography>

        {/* Section 3 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          3. Eligibility & User Accounts
        </Typography>
        <Typography paragraph>
          You must be at least 18 years old and capable of forming a binding
          contract to use the Service. You may be required to create an account
          to access certain features, including the "FlyBnB Pro" subscription.
          You are responsible for maintaining the confidentiality of your
          account password and for all activities that occur under your account.
          You agree to notify us immediately of any unauthorized use of your
          account. User accounts and profiles are managed using Supabase.
        </Typography>

        {/* Section 4 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          4. FlyBnB Pro Subscription
        </Typography>
        <Typography component="div">
          <ul>
            <li>
              <Typography>
                <strong>Features:</strong> FlyBnB offers a premium subscription
                ("FlyBnB Pro") which may include benefits such as unlimited
                searches, access to an AI-powered Airbnb review analyzer,
                advanced search filters, currency selection, inclusion of hotel
                options, access to a Trip Helper AI, and an ad-free experience.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Billing:</strong> FlyBnB Pro is a paid subscription
                managed via Stripe. By subscribing, you authorize recurring
                monthly or annual billing to your chosen payment method.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Cancellation:</strong> You can cancel your subscription
                at any time through your account settings.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Refunds:</strong> No refunds are provided for partial
                subscription periods.
              </Typography>
            </li>
          </ul>
        </Typography>

        {/* Section 5 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          5. Data Accuracy & Third-Party Links
        </Typography>
        <Typography component="div">
          <ul>
            <li>
              <Typography>
                <strong>Data:</strong> Flight and accommodation data, including
                prices, availability, schedules, and amenities, are sourced from
                public data. This data can change rapidly and without notice.
                Flybnb.io does not guarantee the real-time accuracy,
                completeness, or availability of the information displayed.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Booking:</strong> Flybnb.io facilitates searching but
                does not process bookings directly. Users are redirected to
                third-party or service provider websites (e.g., Skyscanner,
                Airbnb) to complete their bookings. Flybnb.io is not a party to
                any transaction you make with these third parties and is not
                responsible for their terms, privacy policies, or actions.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>AI Content:</strong> Features like the AI Review
                Analyzer generate summaries and insights based on third-party
                data and AI services. This content is provided for informational
                purposes only and should not be considered a factual guarantee
                or endorsement.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Session Replay & Analytics:</strong> We use Microsoft
                Clarity to analyze user interactions on our site, including
                clicks, page views, and other behavior to improve and market our
                services. By using this Service, you consent to this data being
                collected and processed by us and Microsoft as described in our{" "}
                <MuiLink component={Link} to="/privacy" color="primary">
                  Privacy Policy
                </MuiLink>
                .
              </Typography>
            </li>
          </ul>
        </Typography>

        {/* Section 6 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          6. Permitted Use & Restrictions
        </Typography>
        <Typography paragraph>
          The Service is intended for personal, non-commercial use only. You
          agree not to:
        </Typography>
        <Typography component="div">
          <ul>
            <li>
              <Typography>
                Use bots, scrapers, or other automated means to access or
                collect data from the Service.
              </Typography>
            </li>
            <li>
              <Typography>
                Reverse-engineer, decompile, or otherwise attempt to discover
                the source code of the Service.
              </Typography>
            </li>
            <li>
              <Typography>
                Resell, reproduce, or redistribute the Service or any data
                obtained from it.
              </Typography>
            </li>
            <li>
              <Typography>
                Use the Service for any unlawful purpose or in a way that
                violates the rights of others.
              </Typography>
            </li>
            <li>
              <Typography>
                Engage in any activity that could damage, disable, or impair the
                Service.
              </Typography>
            </li>
          </ul>
        </Typography>

        {/* Section 7 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          7. Intellectual Property
        </Typography>
        <Typography paragraph>
          All branding, logos, design, code, text, graphics, and other features
          of the Flybnb.io Service are the intellectual property of Flybnb.io or
          its licensors and are protected by copyright, trademark, and other
          laws. You may not use this intellectual property without prior written
          permission.
        </Typography>

        {/* Section 8 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          8. Disclaimers & Limitation of Liability
        </Typography>
        <Typography paragraph>
          The Service is provided "as is" and "as available" without warranties
          of any kind, either express or implied. Flybnb.io disclaims all
          warranties, including, but not limited to, implied warranties of
          merchantability, fitness for a particular purpose, and
          non-infringement.
        </Typography>
        <Typography paragraph>
          Flybnb.io is not liable for any indirect, incidental, special,
          consequential, or punitive damages, or any loss of profits or
          revenues, whether incurred directly or indirectly, or any loss of
          data, use, goodwill, or other intangible losses resulting from (a)
          your access to or use of or inability to access or use the Service;
          (b) any conduct or content of any third party on the Service,
          including third-party booking sites; or (c) unauthorized access, use,
          or alteration of your transmissions or content.
        </Typography>

        {/* Section 9 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          9. Account Termination
        </Typography>
        <Typography paragraph>
          We reserve the right to suspend or terminate your account and access
          to the Service at our sole discretion, without notice, for conduct
          that we believe violates these Terms or is harmful to other users of
          the Service, us, or third parties, or for any other reason.
        </Typography>

        {/* Section 10 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          10. Amendments
        </Typography>
        <Typography paragraph>
          We reserve the right to modify these Terms at any time. We will notify
          users of significant changes. Your continued use of the Service after
          such modifications constitutes your acceptance of the new Terms.
        </Typography>

        {/* Section 11 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          11. Governing Law
        </Typography>
        <Typography paragraph>
          These Terms shall be governed by the laws of the State of Delaware,
          USA, without regard to its conflict of law provisions. Any disputes
          arising from these Terms shall be resolved in the courts located in
          Delaware.
        </Typography>

        {/* Section 12 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          12. Contact Information
        </Typography>
        <Typography paragraph>
          For any questions about these Terms, please contact us at{" "}
          <MuiLink href="mailto:info@flybnb.io">info@flybnb.io</MuiLink>.{" "}
          {/* Updated email based on original file */}
        </Typography>
      </Box>
    </Container>
    </>
  );
}
